// ============================================================
// PortalRepo — /olivium/portals/:portalId/repo (PHASE_2L).
//
// The repo + deployment metadata editor for a portal. Saved metadata is REAL
// (sq_project_portal_context). LIVE GitHub sync is INTEGRATION_DEFERRED and
// GitHub permission automation is GITHUB_PERMISSION_DEFERRED — this surface
// never fakes a sync, a PR status, a build, or a repo permission grant.
// Presentational + SSR-safe.
// ============================================================

import { useState } from 'react'
import CommandCenterShell from '../../shell/CommandCenterShell'
import { CC_INK, CC_MONO } from '../../shell'
import {
  OLIVIUM_ACCENT, REPO_PROVIDERS, CONTEXT_FIELDS,
  type PortalContextRecord, type PortalContextDraft, type RepoProvider,
} from './portalFactory'
import { PORTAL_HONESTY, SectionCard, StateBlock, BackendNotReadyBanner, chipBtn, primaryBtn, inputStyle, fieldLabel } from './PortalKit'

export interface PortalRepoProps {
  portalName?: string
  context: PortalContextRecord | null
  loading: boolean
  error: string | null
  authRequired: boolean
  saving?: boolean
  onHome?: () => void
  onBackCommandCenter?: () => void
  onOpenPortal?: () => void
  onOpenContext?: () => void
  onSave?: (fields: PortalContextDraft) => void | Promise<unknown>
}

function initialDraft(context: PortalContextRecord | null): PortalContextDraft {
  return {
    repo_provider: context?.repo_provider ?? 'github',
    repo_owner: context?.repo_owner ?? '',
    repo_name: context?.repo_name ?? '',
    repo_url: context?.repo_url ?? '',
    default_branch: context?.default_branch ?? '',
    active_branch: context?.active_branch ?? '',
    current_pr_url: context?.current_pr_url ?? '',
    deployment_url: context?.deployment_url ?? '',
    vercel_project_name: context?.vercel_project_name ?? '',
    supabase_project_ref: context?.supabase_project_ref ?? '',
  }
}

export default function PortalRepo(props: PortalRepoProps) {
  const { portalName, context, loading, error, authRequired, saving, onHome, onBackCommandCenter, onOpenPortal, onOpenContext, onSave } = props
  const [draft, setDraft] = useState<PortalContextDraft>(() => initialDraft(context))
  const [seeded, setSeeded] = useState(false)
  const backendNotReady = !!error && /not migrated/i.test(error)

  // Seed the form once the context loads (SSR-safe: only after a real load).
  if (!seeded && context && !loading) { setSeeded(true); setDraft(initialDraft(context)) }

  const set = (k: keyof PortalContextDraft, v: string) => setDraft((d) => ({ ...d, [k]: v }))
  const repoFields = CONTEXT_FIELDS.filter((f) => f.group === 'repo')
  const deployFields = CONTEXT_FIELDS.filter((f) => f.group === 'deploy')

  return (
    <CommandCenterShell
      onHome={onHome} onBackCommandCenter={onBackCommandCenter}
      parentBack={{ label: '← Context', onClick: onOpenContext }}
      breadcrumb={['OLIVIUM', (portalName || 'PORTAL').toUpperCase(), 'REPO']}
      title="Repo & Deployment" titleAccent={OLIVIUM_ACCENT} honestyBadge={PORTAL_HONESTY}
    >
      <div>
        {backendNotReady && <BackendNotReadyBanner message={error!} />}
        {loading && <StateBlock kind="loading" title="Loading repo context…" />}
        {!loading && authRequired && <StateBlock kind="auth" title="Sign in to edit repo context" />}
        {!loading && !authRequired && error && !backendNotReady && <StateBlock kind="error" title="Could not load repo context" body={error} />}

        {!loading && !authRequired && !error && (
          <div>
            <SectionCard title="Deferred integrations" accent="#b8862a">
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: CC_INK.muted, lineHeight: 1.7 }}>
                <li>Live GitHub sync (branches / PRs / build status) — <span style={{ color: '#b8862a', fontFamily: CC_MONO }}>INTEGRATION_DEFERRED</span>. Saved metadata below is real; nothing is auto-synced or faked.</li>
                <li>GitHub repo permission automation — <span style={{ color: '#b8862a', fontFamily: CC_MONO }}>GITHUB_PERMISSION_DEFERRED</span>. No repo permission is granted from here.</li>
              </ul>
            </SectionCard>

            <SectionCard title="Repository">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label>
                  <span style={fieldLabel}>Provider</span>
                  <select style={inputStyle} value={draft.repo_provider ?? 'github'} onChange={(e) => set('repo_provider', e.target.value as RepoProvider)}>
                    {REPO_PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </label>
                <span />
                {repoFields.map((f) => (
                  <label key={f.key as string}>
                    <span style={fieldLabel}>{f.label}</span>
                    <input style={inputStyle} value={(draft[f.key] as string) ?? ''} placeholder={f.placeholder} onChange={(e) => set(f.key, e.target.value)} />
                  </label>
                ))}
              </div>
            </SectionCard>

            <SectionCard title="Deployment">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {deployFields.map((f) => (
                  <label key={f.key as string}>
                    <span style={fieldLabel}>{f.label}</span>
                    <input style={inputStyle} value={(draft[f.key] as string) ?? ''} placeholder={f.placeholder} onChange={(e) => set(f.key, e.target.value)} />
                  </label>
                ))}
              </div>
              <p style={{ margin: '10px 2px 0', fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>
                Supabase project ref is an identifier only — never paste a key or secret here.
              </p>
            </SectionCard>

            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={() => onSave?.(draft)}>{saving ? 'Saving…' : 'Save repo context'}</button>
              <button type="button" style={chipBtn} onClick={onOpenContext}>Back to context</button>
            </div>
          </div>
        )}
      </div>
    </CommandCenterShell>
  )
}
